import { RankedModeTypes } from '../../../../shared/GameEnums.ts';
import { LeaderboardConstants } from '../../../../shared/leaderboard_data/LeaderboardConstants.ts';
import { LeaderboardCategoryStringID } from '../../../../shared/leaderboard_data/LeaderboardEnums.ts';
import { parseLeaderboardsUrlState } from './parseLeaderboardsUrlState.ts';
function createDefaultState() {
    return {
        rankedModeType: RankedModeTypes.Classic4P,
        leaderboardUrl: LeaderboardConstants.globalLeaderboardUrl,
        searchTerm: '',
        tableTabs: [],
        enabledTabIds: [],
        rowData: [],
        myStickyRowData: undefined,
        moreDataAvailable: true,
        noFriendsEmpty: false,
        seasonStartDate: undefined,
        seasonEndDate: undefined
    };
}
export class LeaderboardsPageData {
    subscribe(listener) {
        this.listeners.add(listener);
        return ()=>{
            this.listeners.delete(listener);
        };
    }
    getState() {
        return this.state;
    }
    async init() {
        if (this.parseStateFromUrl) {
            const urlState = parseLeaderboardsUrlState(this.deps.getCurrentUrl());
            this.updateState(urlState);
        }
        this.deps.updateBrowserTitle(this.state.rankedModeType, this.state.leaderboardUrl);
        this.unsubscribePopState = this.deps.subscribePopState((state)=>{
            void this.handlePopState(state);
        });
        await Promise.all([
            this.fetchNewTabs(),
            this.fetchMyStickyRowData(),
            this.fetchScrollOrSearch()
        ]);
    }
    dispose() {
        var _this_unsubscribePopState, _this;
        (_this_unsubscribePopState = (_this = this).unsubscribePopState) === null || _this_unsubscribePopState === void 0 ? void 0 : _this_unsubscribePopState.call(_this);
        this.unsubscribePopState = undefined;
    }
    async changeRankedMode(mode) {
        await this.switchRankingCategoryTypeData(mode, this.state.leaderboardUrl, this.state.searchTerm, true);
        await this.fetchMyStickyRowData();
    }
    async changeLeaderboardUrl(leaderboardUrl) {
        await this.switchRankingCategoryTypeData(this.state.rankedModeType, leaderboardUrl, this.state.searchTerm, true);
        await Promise.all([
            this.fetchNewTabs(),
            this.fetchMyStickyRowData()
        ]);
    }
    async changeSearch(search) {
        await this.switchRankingCategoryTypeData(this.state.rankedModeType, this.state.leaderboardUrl, search, true);
        await this.fetchMyStickyRowData();
    }
    async fetchMoreData() {
        await this.fetchScrollData();
    }
    async refreshStickyRow() {
        await this.fetchMyStickyRowData();
    }
    getMyRowData() {
        const sticky = this.state.myStickyRowData;
        if (sticky == undefined) return undefined;
        const isSearchActive = this.state.searchTerm != LeaderboardConstants.emptyUsernameSearch;
        const isOnFriendsTab = this.state.leaderboardUrl == LeaderboardCategoryStringID.Friends;
        if (isSearchActive || isOnFriendsTab) {
            const myIndexInResults = this.state.rowData.findIndex((row)=>row.myData);
            if (myIndexInResults < 0) return undefined;
            return {
                index: myIndexInResults,
                data: sticky
            };
        }
        const isEmptyRank = sticky.rank == LeaderboardConstants.emptyRank;
        const index = isEmptyRank ? Infinity : sticky.rank - 1;
        return {
            index: index,
            data: sticky
        };
    }
    async switchRankingCategoryTypeData(mode, leaderboardUrl, search, push) {
        this.updateState({
            rankedModeType: mode,
            leaderboardUrl: leaderboardUrl,
            searchTerm: search,
            rowData: [],
            moreDataAvailable: true,
            noFriendsEmpty: false
        });
        if (push) {
            const url = this.buildUrl(mode, leaderboardUrl, search);
            this.deps.pushHistoryState({
                rankedModeType: mode,
                leaderboardUrl: leaderboardUrl,
                searchTerm: search
            }, url);
        }
        this.deps.updateBrowserTitle(mode, leaderboardUrl);
        this.fetchEpoch += 1;
        this.isLoadingDataAfterScroll = false;
        await this.fetchScrollOrSearch();
    }
    async fetchScrollOrSearch() {
        if (this.state.searchTerm == LeaderboardConstants.emptyUsernameSearch) {
            await this.fetchScrollData();
        } else {
            await this.fetchSearchData();
        }
    }
    maskMembership(row) {
        return this.hideMembership ? {
            ...row,
            membershipType: undefined
        } : row;
    }
    buildUrl(mode, leaderboardUrl, search) {
        const searchQuery = search == LeaderboardConstants.emptyUsernameSearch ? '' : `?q=${encodeURIComponent(search)}`;
        const urlPart = leaderboardUrl ? `/${leaderboardUrl}` : '';
        return `${this.deps.getLanguagePathPrefix()}/leaderboards/${RankedModeTypes[mode]}${urlPart}${searchQuery}`;
    }
    async fetchScrollData() {
        const isActiveSearchOngoing = this.state.searchTerm != LeaderboardConstants.emptyUsernameSearch && this.state.rowData.length > 0;
        if (isActiveSearchOngoing) return;
        if (this.isLoadingDataAfterScroll) return;
        this.isLoadingDataAfterScroll = true;
        const myEpoch = this.fetchEpoch;
        const startRank = this.state.rowData.length + 1;
        const endRank = startRank + LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit - 1;
        const data = await this.deps.requestScrollData(this.state.rankedModeType, this.state.leaderboardUrl, startRank, endRank, this.state.searchTerm);
        if (myEpoch != this.fetchEpoch) return;
        this.isLoadingDataAfterScroll = false;
        if (data == undefined) return;
        const isOnFriendsTab = this.state.leaderboardUrl == LeaderboardCategoryStringID.Friends;
        const isFirstPageEmpty = data.length == 0 && this.state.rowData.length == 0;
        if (isOnFriendsTab && isFirstPageEmpty) {
            this.updateState({
                noFriendsEmpty: true,
                moreDataAvailable: false
            });
            return;
        }
        if (data.length == 0) {
            this.updateState({
                moreDataAvailable: false
            });
            return;
        }
        if (!this.isReturnedDataInValidOrder(data)) {
            await this.fetchScrollData();
            return;
        }
        this.updateState({
            rowData: [
                ...this.state.rowData,
                ...data.map((row)=>this.maskMembership(row))
            ],
            moreDataAvailable: data.length == LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit
        });
    }
    isReturnedDataInValidOrder(data) {
        if (this.state.leaderboardUrl == LeaderboardCategoryStringID.Friends) return true;
        const firstRankReturned = data[0].rank;
        const expectedFirstRankReturned = this.state.rowData.length + 1;
        return firstRankReturned == expectedFirstRankReturned;
    }
    async fetchSearchData() {
        const myEpoch = this.fetchEpoch;
        const data = await this.deps.requestSearchData(this.state.rankedModeType, this.state.leaderboardUrl, this.state.searchTerm);
        if (myEpoch != this.fetchEpoch) return;
        if (data == undefined) return;
        this.updateState({
            rowData: data.map((row)=>this.maskMembership(row)),
            moreDataAvailable: false
        });
    }
    async fetchNewTabs() {
        const data = await this.deps.requestTabs(this.state.leaderboardUrl);
        if (data == undefined) return;
        this.updateState({
            tableTabs: data.tableTabs,
            enabledTabIds: data.activeRankedModeTypes.map((mode)=>RankedModeTypes[mode]),
            seasonStartDate: data.activeSeasonData != undefined ? new Date(data.activeSeasonData.startDate) : undefined,
            seasonEndDate: data.activeSeasonData != undefined ? new Date(data.activeSeasonData.endDate) : undefined
        });
    }
    async fetchMyStickyRowData() {
        const data = await this.deps.requestStickyRow(this.state.rankedModeType, this.state.leaderboardUrl);
        this.updateState({
            myStickyRowData: data != undefined ? this.maskMembership(data) : undefined
        });
    }
    async handlePopState(state) {
        if (state == undefined) {
            const urlState = parseLeaderboardsUrlState(this.deps.getCurrentUrl());
            var _urlState_rankedModeType, _urlState_leaderboardUrl, _urlState_searchTerm;
            await this.switchRankingCategoryTypeData((_urlState_rankedModeType = urlState.rankedModeType) !== null && _urlState_rankedModeType !== void 0 ? _urlState_rankedModeType : this.state.rankedModeType, (_urlState_leaderboardUrl = urlState.leaderboardUrl) !== null && _urlState_leaderboardUrl !== void 0 ? _urlState_leaderboardUrl : this.state.leaderboardUrl, (_urlState_searchTerm = urlState.searchTerm) !== null && _urlState_searchTerm !== void 0 ? _urlState_searchTerm : this.state.searchTerm, false);
            return;
        }
        await this.switchRankingCategoryTypeData(state.rankedModeType, state.leaderboardUrl, state.searchTerm, false);
    }
    updateState(partial) {
        this.state = {
            ...this.state,
            ...partial
        };
        for (const listener of this.listeners){
            listener(this.state);
        }
    }
    constructor(deps, options = {}){
        this.listeners = new Set();
        this.isLoadingDataAfterScroll = false;
        this.fetchEpoch = 0;
        this.deps = deps;
        var _options_hideMembership;
        this.hideMembership = (_options_hideMembership = options.hideMembership) !== null && _options_hideMembership !== void 0 ? _options_hideMembership : false;
        this.state = {
            ...createDefaultState(),
            ...options.initialState
        };
        var _options_parseStateFromUrl;
        this.parseStateFromUrl = (_options_parseStateFromUrl = options.parseStateFromUrl) !== null && _options_parseStateFromUrl !== void 0 ? _options_parseStateFromUrl : false;
    }
}
